/**
 * A run's side panel (TRADE-CONS 4e, Pedro 2026-10-30; migration 0133): a consolidation traded as
 * one, stepped as one — the same steps, windows and words as a trade's (TradePanel):
 *
 *   Draft → [Email to client] (one PDF for the agreement, O7) → With client → [Mark approved] →
 *   Approved by client → [Send for execution] per bank (its Excel, a tab per portfolio; S4-Q6: that
 *   bank's portfolios are Sent) → Sent for execution (every bank) → [Mark executed] per portfolio →
 *   Executed (every portfolio)
 *   With client → [Revise] the whole run (a new run, new numbers, S4-Q7); [Cancel run] with a reason
 *   until any portfolio is executed.
 *
 * The run carries the status and its drafts follow it. Each step commits at once through its own
 * window (§3g Pattern B). Every greyed button says why (TF-TIPS). Steps: `trades.draft`.
 */
import { useEffect, useState } from 'react';
import { Anchor, Badge, Button, Divider, Drawer, Group, Stack, Table, Text, Timeline, Tooltip } from '@mantine/core';
import { IconPaperclip } from '@tabler/icons-react';

import { tradesApi, type TradeDraft, type TradeFileRef, type TradeRun, type TradeRunBank } from '../api/trades';
import { at, fail, StepWindow, Why, type Step, type StepTarget } from './TradePanel';
import { TradeStatusBadge } from './TradesList';

type Act = { step: Step; bank?: TradeRunBank; draft?: TradeDraft };
const GOAL_SHORT: Record<string, string> = {
  cross_bank: 'transfers between banks', transfers: 'transfers', trades: 'trades', target: 'closest to target',
  volume: 'traded value', gains: 'realised gains',
};

export function RunPanel({ run: r, onClose, canAct, onOpenRun, onSelectRun, onSelectDraft }: {
  run: TradeRun | null;
  onClose: () => void;
  canAct: boolean;
  /** Open a run in its consolidation's trade mode (a Draft run; a revision just made). */
  onOpenRun: (r: TradeRun) => void;
  onSelectRun: (id: string) => void;
  onSelectDraft: (id: string) => void;
}) {
  const [act, setAct] = useState<Act | null>(null);
  useEffect(() => { setAct(null); }, [r?.id]);
  if (!r) return <Drawer opened={false} onClose={onClose} />;

  const s = r.status;
  const final = s === 'executed' || s === 'cancelled' || s === 'revised';
  const noPerm = canAct ? null : 'Needs the Draft trades permission';
  const banks = r.banks ?? [];
  const executed = r.drafts.filter((d) => d.status === 'executed');
  const file = (f: TradeFileRef | null | undefined, label: string) => f && (
    <Anchor size="xs" onClick={() => tradesApi.runs.openFile(r.id, f).catch((e) => fail('File not opened', e))}>
      <Group gap={4} wrap="nowrap" component="span"><IconPaperclip size={12} />{label}</Group>
    </Anchor>
  );
  const button = (label: string, a: Act, why: string | null = null, color?: string) => (
    <Why why={noPerm ?? why}>
      <Button size="xs" color={color} variant={color ? 'light' : 'filled'} disabled={!!noPerm || !!why}
        onClick={() => setAct(a)}>{label}</Button>
    </Why>
  );
  const target = act ? runTarget(r, act, onOpenRun) : null;

  return (
    <Drawer opened onClose={onClose} position="right" size={560} title={
      <Group gap="xs"><Text fw={700}>{r.serial}</Text>
        <TradeStatusBadge t={{ status: r.status, status_label: r.status_label } as TradeDraft} />
        <Badge size="sm" variant="outline" color="gray" style={{ textTransform: 'none' }}>Run</Badge></Group>}>
      <Stack gap="sm">
        <div>
          <Text size="sm" fw={600}>{r.title ?? 'Untitled'}</Text>
          <Text size="xs" c="dimmed">Consolidation {r.consolidation_name ?? '?'}{r.consolidation_deleted ? ' (deleted)' : ''} · {r.agreement_label ?? '-'}</Text>
          <Text size="xs" c="dimmed">A consolidation traded as one: {r.drafts.length} portfolio{r.drafts.length === 1 ? '' : 's'}, each with its own trade number, stepped together here.</Text>
          {/* 5c (S5-Q10): the rules and goal order its split was optimized with. */}
          {r.optimizer_used ? (
            <Tooltip withArrow multiline w={380} label={
              <Stack gap={0}>
                <Text size="xs">Goal order: {r.optimizer_used.ranking.map((g) => GOAL_SHORT[g] ?? g).join(' → ')}</Text>
                {r.optimizer_used.members.map((m) => (
                  <Text key={m.label} size="xs">{m.label}: minimum cash {m.min_cash.toLocaleString()} {m.ccy}{m.wrapper ? ` · wrapper ${m.wrapper}` : ''}</Text>
                ))}
              </Stack>}>
              <Text size="xs" c="teal.8">Split optimized: {r.optimizer_used.preset_label}, tolerance {r.optimizer_used.tolerance_bps} bps · {at(r.optimizer_used.at)}</Text>
            </Tooltip>
          ) : <Text size="xs" c="dimmed">Split as typed (not optimized).</Text>}
        </div>
        <Group gap="xs">
          <Text size="xs" fw={600}>Documents:</Text>
          {(r.files ?? []).length ? (<>
            {(r.files ?? []).map((f) => (
              <Anchor key={f.id} size="xs" onClick={() => tradesApi.runs.openFile(r.id, f).catch((e) => fail('Document not opened', e))}>
                {f.kind === 'client_pdf' ? 'PDF' : `Excel ${f.bank ?? ''}`.trim()}
              </Anchor>
            ))}
            <Text size="xs" c="dimmed">made {at(r.documents_at)}</Text>
          </>) : <Text size="xs" c="dimmed">none yet: save the run in trade mode to make them</Text>}
        </Group>

        <Divider label="Next step" labelPosition="left" />
        {final ? <Text size="sm" c="dimmed">None: this run is {r.status_label}.</Text> : (
          <Group gap="xs">
            {s === 'draft' && (<>
              <Why why={noPerm ?? (r.consolidation_deleted ? 'Its consolidation was deleted: the run can no longer be changed' : null)}>
                <Button size="xs" variant="default" disabled={!!noPerm || r.consolidation_deleted} onClick={() => onOpenRun(r)}>Open in trade mode</Button>
              </Why>
              {button('Email to client', { step: 'client_email' },
                !r.documents_at ? 'Save the run in trade mode first: the email attaches its PDF' : null)}
            </>)}
            {s === 'with_client' && (<>{button('Mark approved', { step: 'approve' })}{button('Revise', { step: 'revise' }, null, 'orange')}</>)}
            {(s === 'approved' || s === 'sent') && <Text size="xs" c="dimmed">Send each bank its orders, then mark each portfolio executed (below).</Text>}
            {button('Cancel run', { step: 'cancel' }, executed.length
              ? `${executed.map((d) => d.serial).join(', ')} already executed: the run can no longer be cancelled` : null, 'red')}
          </Group>
        )}

        {(s === 'approved' || s === 'sent' || s === 'executed') && (<>
          <Divider label="Banks" labelPosition="left" />
          <Table withTableBorder fz="xs" verticalSpacing={4}>
            <Table.Tbody>
              {banks.map((b) => (
                <Table.Tr key={b.bank_id}>
                  <Table.Td><Text size="xs" fw={600}>{b.bank}{b.bank_name ? ` · ${b.bank_name}` : ''}</Text>
                    <Text size="xs" c="dimmed">{b.portfolios.join(', ')}</Text></Table.Td>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>
                    {b.sent_at ? (<Stack gap={0}>
                      <Text size="xs">Sent · {b.sent_by_name ?? '-'} · {at(b.sent_at)}</Text>{file(b.email_file, 'The email sent')}
                    </Stack>) : button('Send for execution', { step: 'bank_email', bank: b },
                      !b.excel_file_id ? "This bank's Excel is missing: save the run again" : null)}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>)}

        <Divider label="Portfolios" labelPosition="left" />
        <Table withTableBorder fz="xs" verticalSpacing={4}>
          <Table.Tbody>
            {r.drafts.map((d) => (
              <Table.Tr key={d.id}>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>
                  <Anchor size="xs" fw={600} onClick={() => onSelectDraft(d.id)}>{d.serial}</Anchor>
                </Table.Td>
                <Table.Td>{d.portfolio_label ?? d.portfolio}<Text span size="xs" c="dimmed"> · {d.line_count} order{d.line_count === 1 ? '' : 's'}{d.transfer_count ? `, ${d.transfer_count} transfer${d.transfer_count === 1 ? '' : 's'}` : ''}</Text></Table.Td>
                {/* Smoke 2026-11-01: the badge was cut to "Sent for exe…" — its cell keeps the full label (§3j). */}
                <Table.Td style={{ whiteSpace: 'nowrap', minWidth: 150 }}><TradeStatusBadge t={d} /></Table.Td>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>
                  {d.status === 'executed' ? <Text size="xs" c="dimmed">{d.executed_by_name ?? '-'} · {at(d.executed_at)}</Text>
                    : (s === 'approved' || s === 'sent') ? button('Mark executed', { step: 'execute', draft: d },
                      d.status !== 'sent' ? "Send its bank's orders first" : null) : null}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        <Divider label="History" labelPosition="left" />
        <Timeline bulletSize={14} lineWidth={2} active={99}>
          <Timeline.Item title="Created"><Text size="xs" c="dimmed">{r.created_by_name ?? '-'} · {at(r.created_at)}</Text>
            {r.revised_from && <Text size="xs">Revision of <Anchor size="xs" onClick={() => onSelectRun(r.revised_from!.id)}>{r.revised_from.serial}</Anchor></Text>}
            <Text size="xs" c="dimmed">Last saved: {r.updated_by_name ?? '-'} · {at(r.updated_at)}</Text>
          </Timeline.Item>
          {r.client_pending_at && <Timeline.Item title="Emailed to client">
            <Text size="xs" c="dimmed">{r.client_pending_by_name ?? '-'} · {at(r.client_pending_at)}</Text>{file(r.client_email, 'The email sent')}
          </Timeline.Item>}
          {r.approved_at && <Timeline.Item title="Approved by client" color="teal">
            <Text size="xs" c="dimmed">Marked by {r.approved_by_name ?? '-'} · {at(r.approved_at)}</Text>
            {r.approved_note && <Text size="xs">“{r.approved_note}”</Text>}{file(r.approval_file, 'Evidence')}
          </Timeline.Item>}
          {r.rejected_at && <Timeline.Item title={s === 'revised' ? 'Revised' : 'Rejected by client'} color="orange">
            <Text size="xs" c="dimmed">{r.rejected_by_name ?? '-'} · {at(r.rejected_at)}</Text>
            {r.cancel_reason && <Text size="xs">Reason: {r.cancel_reason}</Text>}
            {s === 'revised' && r.revised_as && <Text size="xs">Continued as <Anchor size="xs" onClick={() => onSelectRun(r.revised_as!.id)}>{r.revised_as.serial}</Anchor></Text>}
          </Timeline.Item>}
          {banks.filter((b) => b.sent_at).map((b) => (
            <Timeline.Item key={b.bank_id} title={`Sent for execution · ${b.bank}`}>
              <Text size="xs" c="dimmed">{b.sent_by_name ?? '-'} · {at(b.sent_at)}</Text>{file(b.email_file, 'The email sent')}
            </Timeline.Item>
          ))}
          {executed.map((d) => (
            <Timeline.Item key={d.id} title={`Executed · ${d.serial} (${d.portfolio_label ?? d.portfolio})`} color="green">
              <Text size="xs" c="dimmed">Marked by {d.executed_by_name ?? '-'} · {at(d.executed_at)}</Text>
            </Timeline.Item>
          ))}
          {r.cancelled_at && <Timeline.Item title="Cancelled" color="red">
            <Text size="xs" c="dimmed">{r.cancelled_by_name ?? '-'} · {at(r.cancelled_at)}</Text>
            {!r.rejected_at && r.cancel_reason && <Text size="xs">Reason: {r.cancel_reason}</Text>}
          </Timeline.Item>}
        </Timeline>
      </Stack>

      {target && <StepWindow step={act!.step} target={target} onClose={() => setAct(null)} />}
    </Drawer>
  );
}

/** The run (or one bank / one portfolio of it) as a step window's target. */
function runTarget(r: TradeRun, a: Act, onOpenRun: (r: TradeRun) => void): StepTarget {
  const bank = a.bank;
  const draft = a.draft;
  return {
    key: bank ? `${r.id}:${bank.bank_id}` : r.id,
    serial: draft ? `${draft.serial} (${draft.portfolio_label ?? draft.portfolio})` : r.serial,
    noun: 'run',
    bankNote: bank ? `${bank.bank}'s portfolio${bank.portfolios.length === 1 ? '' : 's'} ${bank.portfolios.join(', ')}` : undefined,
    emailOptions: async () => {
      const o = await tradesApi.runs.emailOptions(r.id);
      const b = o.banks.find((x) => x.bank_id === bank?.bank_id);
      return { client: o.client, bank: { bank: b?.bank ?? null, to: b?.to ?? [], cc: b?.cc ?? [] } };
    },
    prepareEmail: (kind, ids) => (kind === 'client' ? tradesApi.runs.prepareClientEmail(r.id, ids)
      : tradesApi.runs.prepareBankEmail(r.id, bank!.bank_id, ids)),
    emailSent: (kind, file) => (kind === 'client' ? tradesApi.runs.clientEmailSent(r.id, file)
      : tradesApi.runs.bankEmailSent(r.id, bank!.bank_id, file)),
    approve: (note, evidence) => tradesApi.runs.approve(r.id, note, evidence),
    revise: async (reason) => {
      const out = await tradesApi.runs.reject(r.id, reason, 'revise');
      if (out.new_run) onOpenRun(out.new_run);
      return out.new_run?.serial ?? null;
    },
    execute: () => tradesApi.runs.execute(r.id, draft!.id),
    cancel: (reason) => tradesApi.runs.cancel(r.id, reason),
  };
}
